export const marketingAutomation = {
  seo: {
    title: "Marketing & social — Bespire AI assistant",
    description:
      "Posts, campaigns, emails, ads — the Bespire AI writes them, ships them, measures them. Unlimited AI, with fair use.",
  },
  hero: {
    badge: "Marketing & social",
    title: "Marketing that runs like you had a team",
    subtitle:
      "The assistant plans your posts weekly, writes your ads, sends your email campaigns — in your brand voice, aware of your calendar. Unlimited AI, with fair use.",
    ctaTrial: "Try it free for 3 days",
    ctaPricing: "See pricing →",
    composer: {
      label: "Post composer · preview",
      channelInstagram: "Instagram",
      channelFacebook: "Facebook",
      channelEmail: "Email",
      caption:
        "Prenatal yoga is back on Thursday at Bella Studio 💜 3 spots left — link in bio to book.",
      hashtags: ["#prenatalyoga", "#bellastudio", "#budapest"],
      image: "Studio · natural light · yoga mat",
      scheduleLabel: "Scheduled",
      scheduleValue: "Tue 6:30 pm · Instagram + Facebook",
      cta: "Approve schedule",
    },
  },
  process: {
    title: "Three steps to marketing that runs itself",
    subtitle:
      "Same assistant that runs your booking and website. Not a separate tool — it works from shared data.",
    steps: [
      {
        step: "01",
        icon: "Compass",
        title: "A plan, not random posts",
        description:
          "Every week the Bespire AI drafts a plan: what to post, which campaign to send, when. Based on your calendar and pricing.",
        bullets: [
          "Weekly content plan for Instagram, Facebook, email",
          "Seasonal campaigns auto-scheduled",
          "Messages timed against your booking calendar",
        ],
      },
      {
        step: "02",
        icon: "PenSquare",
        title: "It writes and ships",
        description:
          "Captions, hashtags, image concept, email templates, ad variants. One preview, one approval — it goes live.",
        bullets: [
          "Posts written in your brand voice",
          "Image concepts and templates",
          "Ad variants for A/B testing",
        ],
      },
      {
        step: "03",
        icon: "BarChart3",
        title: "It measures and learns",
        description:
          "It sees which post drove bookings. Not impressions — bookings. Next time it doubles down on the winning direction.",
        bullets: [
          "Measurement tied back to revenue",
          "Which channel brings real clients",
          "Auto-suggested reruns of what worked",
        ],
      },
    ],
  },
  beforeAfter: {
    title: "Before / after: same week",
    subtitle:
      "Not more work — just less random presence.",
    before: {
      label: "Before",
      businessName: "Ad-hoc marketing",
      tagline: "Sunday-night panic that there's no post ready.",
      bio: "Posting happens when there's time. Haven't emailed the list in months. Tried an ad once, no idea what it did.",
      problems: [
        "Posts only when you have time",
        "Email list sits unused",
        "Ads run without knowing what they did",
        "Nothing connects back to your bookings",
      ],
    },
    after: {
      label: "After",
      businessName: "Marketing from Bespire",
      tagline: "Weekly plan, content shipped, measured results.",
      bio: "Monday you have a plan, Tuesday posts to approve, Thursday the email campaign, Sunday a weekly report of what drove bookings.",
      wins: [
        "Weekly content plan to approve",
        "Auto-scheduled across every channel",
        "Measurement tied to bookings",
        "Seasonal campaigns booked ahead",
      ],
    },
  },
  showcase: {
    title: "A weekly plan by day seven",
    subtitle:
      "Instagram, Facebook, email — the assistant plans ahead, you just approve.",
    days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    channels: [
      {
        name: "Instagram",
        icon: "Instagram",
        posts: [
          "", "Reel · Prenatal yoga snippet", "Story · Client review", "", "Feed · Weekend package", "Story · Class capacity", "",
        ],
      },
      {
        name: "Facebook",
        icon: "Facebook",
        posts: [
          "", "Post · New Thursday class", "", "Post · Blog: relaxation", "", "Event · Saturday 10:00", "",
        ],
      },
      {
        name: "Email",
        icon: "Mail",
        posts: [
          "", "", "Campaign · Weekend offer", "", "", "", "Monthly newsletter",
        ],
      },
    ],
    campaigns: {
      title: "One message, a whole campaign",
      subtitle: "\"Run a weekend deal for new clients.\" — the assistant turns it into the full package.",
      items: [
        { icon: "PenSquare", title: "3 post variants", note: "Insta feed, Insta reel, Facebook — distinct voice for each" },
        { icon: "Mail", title: "Email campaign", note: "A/B subject lines, segmented for lapsed clients" },
        { icon: "Megaphone", title: "Facebook ad", note: "2 creatives, 2 audiences, suggested daily budget" },
        { icon: "BarChart3", title: "Weekly report", note: "What drove bookings, what to rerun" },
      ],
    },
    fairUse: "Unlimited AI, with fair use — for normal daily volume you don't have to worry about limits.",
    fairUseDetail: "In practice that typically means 3–5 posts per day and 2–3 campaigns per month — if you need more, we sort it out together.",
  },
  chat: {
    title: "Ask for a campaign like you'd ask a team",
    subtitle:
      "Except it's a one-line chat, not a week-long brief.",
    chat: {
      title: "Marketing assistant",
      online: "Online",
      messages: [
        { role: "user" as const, content: "Run a weekend deal for new clients — 20% off Saturday and Sunday." },
        { role: "assistant" as const, content: "Got it. Here's what I put together:", list: [
          "📸 3 Instagram posts (feed + 1 reel), in your brand voice",
          "📧 Email campaign to lapsed clients — no booking in 3+ months",
          "🎯 Facebook ad: 2 creatives, 2 audiences (moms + working parents)",
          "🗓 Site banner + landing page for the deal",
        ] },
        { role: "user" as const, content: "Give each email recipient a unique code." },
        { role: "assistant" as const, content: "Done: personal code per recipient, valid Sat–Sun only, tied to a booking. Preview?" },
        { role: "user" as const, content: "Preview, then ship it." },
        { role: "assistant" as const, content: "Preview sent. On approval I'll schedule everything and send you a results report Monday. 🚀" },
      ],
    },
  },
  crossLinks: {
    title: "Marketing doesn't stop here",
    subtitle: "Same assistant plans your bookings, writes your site copy and handles your customer relationships.",
  },
  cta: {
    title: "Start your marketing today",
    subtitle: "3-day free trial, no card required.",
    primary: "Try it free for 3 days",
    secondary: "See pricing",
  },
};
